import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
}

// Every BE response is wrapped in the app-standard `StandardResponse` envelope
// (`{ code, message, data, errors, meta }`) — the real payload lives in `.data`.
// e.g. the list endpoint returns `{ code, message, data: { packages, total, … } }`,
// so the packages are at `res.data.data.packages`, NOT `res.data.packages`.
// Unwrap one level (defensive: if a response ever arrives unwrapped, use as-is).
function unwrapEnvelope<T>(resData: unknown): T {
    if (
        resData &&
        typeof resData === "object" &&
        "data" in resData &&
        "code" in resData
    ) {
        return (resData as { data: T }).data;
    }
    return resData as T;
}

// Platforms a package can belong to (BE contract #3197).
export type AdminPackagePlatform = "recruiter" | "training" | "agent";

export const ADMIN_PACKAGE_PLATFORMS: AdminPackagePlatform[] = [
    "recruiter",
    "training",
    "agent",
];

// `features` is a free-form JSON map on the BE — kept loose on the FE (the shape
// is package-specific, not a fixed schema).
export type AdminPackageFeatures = Record<string, unknown>;

// Currency codes a package can carry pricing for. SGD/MYR are Stripe-syncable
// (separate Stripe accounts, BE #3159); IDR is stored/displayed only.
export const ADMIN_PACKAGE_CURRENCIES = ["SGD", "MYR", "IDR"] as const;
export type AdminPackageCurrency = (typeof ADMIN_PACKAGE_CURRENCIES)[number];

// One currency's pricing block: a map of pricing field → amount. The fields are
// PLATFORM-DEPENDENT (like `features`): all platforms carry the base
// `price_month` / `price` / `discount` / `tax`, plus that platform's add-on
// prices — recruiter adds `cv_screening_extra_price` + `ai_assessment_extra_price`
// (training/agent add-ons are TBD). `price` is the admin-set TOTAL (NOT derived
// from `price_month`); `discount`/`tax` are percentages. The BE normalises a
// missing field to 0 and drops unknown keys.
export type AdminPackageCurrencyDetail = Record<string, number>;

// `details` is a PER-CURRENCY map: key = currency code, value = the pricing block.
export type AdminPackageDetails = Record<string, AdminPackageCurrencyDetail>;

// FE-facing (camelCase) shape, mapped from the BE snake_case `Package`.
export interface AdminSubscriptionPackage {
    id: string;
    packageName: string;
    description: string | null;
    platform: AdminPackagePlatform;
    isActive: boolean;
    // "Shadow" package (BE `is_shadow_package`, #3004): created but hidden from
    // the public catalog (GET /subscriptions/packages filters these server-side).
    // Default false = visible.
    isShadowPackage: boolean;
    // Billing duration of the package, in MONTHS (BE `duration`). Null when unset.
    duration: number | null;
    // Display sort order (BE `sort_order`); the list is ordered sort_order ASC.
    sortOrder: number | null;
    features: AdminPackageFeatures;
    details: AdminPackageDetails;
    // All null until synced by the Stripe sync endpoint (BE #3198).
    // `stripeProductId` is the SG back-compat mirror; `stripeProductIds`
    // carries the per-region Product ids (`{ SG, MY }`) — SG and MY are
    // separate Stripe accounts (BE decision A / #3159).
    stripeProductId: string | null;
    stripeProductIds: Record<string, string> | null;
    // Per-currency recurring Price ids (`{ SGD, MYR }`), each in its account.
    stripePriceIds: Record<string, string> | null;
    // When the package was last synced to Stripe (BE #3198). Null until synced.
    syncedAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

// BE snake_case `Package` (canonical response shape, BE #3197 / #3198).
interface RawAdminPackage {
    id: string;
    package_name: string;
    description: string | null;
    platform: AdminPackagePlatform;
    is_active: boolean;
    is_shadow_package?: boolean;
    duration: number | null;
    sort_order: number | null;
    features: AdminPackageFeatures | null;
    details: AdminPackageDetails | null;
    stripe_product_id: string | null;
    stripe_product_ids: Record<string, string> | null;
    stripe_price_ids: Record<string, string> | null;
    synced_at: string | null;
    created_at: string | null;
    updated_at: string | null;
}

interface RawListResponse {
    packages: RawAdminPackage[];
    total: number;
    page: number;
    page_size: number;
}

export interface ListAdminPackagesParams {
    platform?: AdminPackagePlatform;
    includeArchived?: boolean;
    page: number;
    pageSize: number;
}

export interface AdminPackagesListResult {
    items: AdminSubscriptionPackage[];
    total: number;
    page: number;
    pageSize: number;
}

// Map the BE snake_case `Package` → camelCase. Defensive on the free-form JSON
// maps (default to `{}`) so callers never have to null-check `features.x`.
function mapPackage(raw: RawAdminPackage): AdminSubscriptionPackage {
    return {
        id: raw.id,
        packageName: raw.package_name,
        description: raw.description ?? null,
        platform: raw.platform,
        isActive: Boolean(raw.is_active),
        isShadowPackage: Boolean(raw.is_shadow_package),
        duration: raw.duration ?? null,
        sortOrder: raw.sort_order ?? null,
        features: raw.features ?? {},
        details: raw.details ?? {},
        stripeProductId: raw.stripe_product_id ?? null,
        stripeProductIds: raw.stripe_product_ids ?? null,
        stripePriceIds: raw.stripe_price_ids ?? null,
        syncedAt: raw.synced_at ?? null,
        createdAt: raw.created_at ?? null,
        updatedAt: raw.updated_at ?? null,
    };
}

// GET /admin/subscription-packages — admin/super_admin only (RBAC enforced BE-side).
// `platform` omitted = all platforms; `include_archived` default false.
export async function listAdminPackages(
    params: ListAdminPackagesParams
): Promise<AdminPackagesListResult> {
    const query = new URLSearchParams();
    if (params.platform) query.set("platform", params.platform);
    if (params.includeArchived) query.set("include_archived", "true");
    query.set("page", String(params.page));
    query.set("page_size", String(params.pageSize));

    try {
        const res = await axios.get(
            `${apiURL}/admin/subscription-packages?${query.toString()}`,
            { headers: authHeaders() }
        );
        const body =
            unwrapEnvelope<RawListResponse>(res.data) ??
            ({} as RawListResponse);
        const rawPackages = Array.isArray(body.packages) ? body.packages : [];
        return {
            items: rawPackages.map(mapPackage),
            total: typeof body.total === "number" ? body.total : rawPackages.length,
            page: typeof body.page === "number" ? body.page : params.page,
            pageSize:
                typeof body.page_size === "number" ? body.page_size : params.pageSize,
        };
    } catch (err) {
        catchErrorLog(
            `[admin-subscription-packages] list failed: ${
                err instanceof Error ? err.message : String(err)
            }`
        );
        throw err;
    }
}

// Create / edit payload (camelCase). Mapped to the BE snake_case body. The
// free-form `features` / `details` maps are sent as-is. `platform` is required
// on create; everything is optional on PATCH (partial update, BE #3197).
export interface AdminPackagePayload {
    packageName: string;
    description?: string | null;
    platform: AdminPackagePlatform;
    isActive?: boolean;
    /** Hidden from the public catalog (BE `is_shadow_package`, #3004). Omit on
     *  PATCH to leave unchanged; defaults to false (visible) on create. */
    isShadowPackage?: boolean;
    /** Billing duration in MONTHS (BE `duration`). Null clears it. */
    duration?: number | null;
    /** Display sort order (BE `sort_order`); lower sorts first. Null clears it. */
    sortOrder?: number | null;
    features?: AdminPackageFeatures;
    details?: AdminPackageDetails;
}

// Map the camelCase payload → the BE snake_case create/update body. Only sends
// the keys that are present (partial-friendly for PATCH).
function toRawPayload(
    payload: Partial<AdminPackagePayload>
): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    if (payload.packageName !== undefined) body.package_name = payload.packageName;
    if (payload.description !== undefined) body.description = payload.description;
    if (payload.platform !== undefined) body.platform = payload.platform;
    if (payload.isActive !== undefined) body.is_active = payload.isActive;
    if (payload.isShadowPackage !== undefined) body.is_shadow_package = payload.isShadowPackage;
    if (payload.duration !== undefined) body.duration = payload.duration;
    if (payload.sortOrder !== undefined) body.sort_order = payload.sortOrder;
    if (payload.features !== undefined) body.features = payload.features;
    if (payload.details !== undefined) body.details = payload.details;
    return body;
}

// The BE wraps every response in the app-standard `StandardResponse` envelope
// (`{ code:<int>, message, errors:[…] }`, integer `code` → HTTP status). On a
// CRUD/sync error Axios rejects with that envelope in `err.response.data`.
// Pull out the most useful human message (first `errors[]` entry, else
// `message`) so the UI can show it inline / in a snackbar.
function extractApiMessage(err: unknown): string | null {
    if (axios.isAxiosError(err)) {
        const data = err.response?.data as
            | { message?: string; errors?: Array<string | { message?: string }> }
            | undefined;
        const first = data?.errors?.[0];
        if (typeof first === "string" && first.trim()) return first;
        if (first && typeof first === "object" && first.message?.trim()) {
            return first.message;
        }
        if (data?.message?.trim()) return data.message;
    }
    return null;
}

// HTTP status of an Axios error, or null. Lets the UI distinguish the
// documented Stripe-sync failures (400 no price / 409 disabled / 502 Stripe).
function errorStatus(err: unknown): number | null {
    if (axios.isAxiosError(err)) return err.response?.status ?? null;
    return null;
}

// Thrown by `createPackage` / `updatePackage` / `syncPackageToStripe` so the UI
// can branch on `status` (e.g. 409 = duplicate name / Stripe billing disabled)
// and show the BE's `message` verbatim.
export class AdminPackageError extends Error {
    status: number | null;
    constructor(message: string, status: number | null) {
        super(message);
        this.name = "AdminPackageError";
        this.status = status;
    }
}

function toAdminPackageError(err: unknown, fallback: string): AdminPackageError {
    return new AdminPackageError(
        extractApiMessage(err) ??
            (err instanceof Error ? err.message : fallback),
        errorStatus(err)
    );
}

// GET /admin/subscription-packages/{id} — single package (404 if missing).
export async function getAdminPackage(
    id: string
): Promise<AdminSubscriptionPackage> {
    try {
        const res = await axios.get<RawAdminPackage>(
            `${apiURL}/admin/subscription-packages/${id}`,
            { headers: authHeaders() }
        );
        return mapPackage(unwrapEnvelope<RawAdminPackage>(res.data));
    } catch (err) {
        catchErrorLog(
            `[admin-subscription-packages] get failed (${id}): ${
                err instanceof Error ? err.message : String(err)
            }`
        );
        throw toAdminPackageError(err, "Could not load the package.");
    }
}

// POST /admin/subscription-packages → 201 + created package (BE #3197).
// 409 if `package_name` collides (global uniqueness).
export async function createPackage(
    payload: AdminPackagePayload
): Promise<AdminSubscriptionPackage> {
    try {
        const res = await axios.post<RawAdminPackage>(
            `${apiURL}/admin/subscription-packages`,
            toRawPayload(payload),
            { headers: authHeaders() }
        );
        return mapPackage(unwrapEnvelope<RawAdminPackage>(res.data));
    } catch (err) {
        catchErrorLog(
            `[admin-subscription-packages] create failed: ${
                err instanceof Error ? err.message : String(err)
            }`
        );
        throw toAdminPackageError(err, "Could not create the package.");
    }
}

// PATCH /admin/subscription-packages/{id} → 200 + updated package (partial,
// BE #3197). 404 if missing, 409 on a name collision.
export async function updatePackage(
    id: string,
    payload: Partial<AdminPackagePayload>
): Promise<AdminSubscriptionPackage> {
    try {
        const res = await axios.patch<RawAdminPackage>(
            `${apiURL}/admin/subscription-packages/${id}`,
            toRawPayload(payload),
            { headers: authHeaders() }
        );
        return mapPackage(unwrapEnvelope<RawAdminPackage>(res.data));
    } catch (err) {
        catchErrorLog(
            `[admin-subscription-packages] update failed (${id}): ${
                err instanceof Error ? err.message : String(err)
            }`
        );
        throw toAdminPackageError(err, "Could not update the package.");
    }
}

// DELETE /admin/subscription-packages/{id} → soft-archive (sets is_active=false)
// so existing subscriptions referencing the package aren't orphaned. The BE
// contract documents a 204, but this app's StandardResponse middleware returns
// 200 + the archived package on the implemented endpoint (BE #3197) — Axios
// resolves on both, so we treat either as success and don't read the body
// (the list refetch re-reads the archived state). Re-activate via
// updatePackage(id, { isActive: true }) (PATCH), not a separate endpoint.
// 404 if the package is missing. Errors surface as AdminPackageError, matching
// createPackage / updatePackage / syncPackageToStripe.
export async function archiveAdminPackage(id: string): Promise<void> {
    try {
        await axios.delete(`${apiURL}/admin/subscription-packages/${id}`, {
            headers: authHeaders(),
        });
    } catch (err) {
        catchErrorLog(
            `[admin-subscription-packages] archive failed (${id}): ${
                err instanceof Error ? err.message : String(err)
            }`
        );
        throw toAdminPackageError(err, "Could not archive the package.");
    }
}

// POST /admin/subscription-packages/{id}/stripe-sync → 200 + the full updated
// package (with `stripe_product_ids` + `stripe_price_ids` populated) (BE #3198).
// Documented failures surfaced via `AdminPackageError.status`:
//   400 — `details.price_month` missing / 0
//   409 — Stripe billing disabled (`ENABLE_STRIPE_BILLING` off)
//   502 — Stripe API failure (message carries the Stripe error)
// `currencies` is optional; the BE defaults to ["SGD","MYR"].
export async function syncPackageToStripe(
    id: string,
    currencies?: string[]
): Promise<AdminSubscriptionPackage> {
    const body =
        currencies && currencies.length > 0 ? { currencies } : {};
    try {
        const res = await axios.post<RawAdminPackage>(
            `${apiURL}/admin/subscription-packages/${id}/stripe-sync`,
            body,
            { headers: authHeaders() }
        );
        return mapPackage(unwrapEnvelope<RawAdminPackage>(res.data));
    } catch (err) {
        catchErrorLog(
            `[admin-subscription-packages] stripe-sync failed (${id}): ${
                err instanceof Error ? err.message : String(err)
            }`
        );
        throw toAdminPackageError(
            err,
            "Could not sync this package to Stripe."
        );
    }
}
